import { createBrowserRouter, Navigate } from 'react-router'

import { AppShell } from '@/components/layout/AppShell'
import { SettingsLayout } from '@/components/layout/SettingsLayout'
import { PlatformLayout } from '@/features/platform/components/PlatformLayout'
import { Lazy } from './Lazy'
import * as Pages from './lazyPages'
import { NotFoundPage } from './NotFoundPage'
import {
  AnonymousOnlyRoute,
  OnboardingRoute,
  PlatformRoute,
  ProtectedRoute,
} from './ProtectedRoute'
import { RouteErrorBoundary } from './RouteErrorBoundary'

/** Kortere skrivemåde for en dovent indlæst side. */
function page(Component: React.ComponentType) {
  return (
    <Lazy>
      <Component />
    </Lazy>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/app/dashboard" replace />,
  },

  // --- Offentlige adgangssider -----------------------------------------------
  // Bekræftelse af e-mail, nulstilling og invitationer ligger uden for
  // AnonymousOnlyRoute: en logget ind bruger skal også kunne åbne dem.
  {
    element: <AnonymousOnlyRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/login', element: page(Pages.LoginPage) },
      { path: '/register', element: page(Pages.RegisterPage) },
      { path: '/glemt-adgangskode', element: page(Pages.ForgotPasswordPage) },
    ],
  },
  {
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/nulstil-adgangskode', element: page(Pages.ResetPasswordPage) },
      { path: '/bekraeft-email', element: page(Pages.ConfirmEmailPage) },
      { path: '/invitationer/:token', element: page(Pages.AcceptInvitationPage) },

      // Delelink udefra. Modtageren har ingen konto, så ruten må hverken
      // kræve login eller sende en indlogget bruger videre.
      { path: '/delt/:token', element: page(Pages.SharedDocumentPage) },
    ],
  },

  // --- Onboarding ------------------------------------------------------------
  {
    element: <OnboardingRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/onboarding/organisation', element: page(Pages.CreateOrganizationPage) },
    ],
  },

  // --- Appen ------------------------------------------------------------------
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/app',
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/app/dashboard" replace /> },
          { path: 'dashboard', element: page(Pages.DashboardPage) },

          { path: 'projects', element: page(Pages.ProjectsPage) },
          { path: 'projects/new', element: page(Pages.CreateProjectPage) },
          { path: 'projects/:projectId', element: page(Pages.ProjectDetailPage) },

          { path: 'godkendelser', element: page(Pages.MyApprovalsPage) },

          // Modulerne set på tværs af projekter. De findes også som en fane
          // inde på hvert projekt; det er samlingen, der ligger her.
          //
          // Kun lister. Oprettelse og rettelse bliver på projektet, hvor
          // rettigheden afgøres — se CrossProjectControllers på serveren.
          { path: 'documents', element: page(Pages.DocumentsPage) },
          { path: 'drawings', element: page(Pages.DrawingsPage) },
          { path: 'tenders', element: page(Pages.TendersPage) },
          { path: 'tasks', element: page(Pages.TasksPage) },
          { path: 'quality', element: page(Pages.QualityPage) },
          { path: 'communication', element: page(Pages.CommunicationPage) },
          { path: 'sustainability', element: page(Pages.SustainabilityPage) },
          { path: 'operations', element: page(Pages.OperationsPage) },

          // Rapporter er undtagelsen: den findes kun her og har ingen
          // projektfane. Genereringen sker for ét projekt, men listen er den
          // eneste indgang til modulet.
          { path: 'reports', element: page(Pages.ReportsPage) },
          { path: 'reports/:reportId', element: page(Pages.ReportDetailPage) },

          { path: 'hjaelp', element: page(Pages.HelpPage) },

          // Indstillingerne deler en fanerække. Den ligger som layout-rute, så
          // siderne selv slipper for at kende hinanden.
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <Navigate to="/app/settings/profile" replace /> },
              { path: 'profile', element: page(Pages.ProfileSettingsPage) },
              { path: 'security', element: page(Pages.SecuritySettingsPage) },
              { path: 'organization', element: page(Pages.OrganizationSettingsPage) },
              { path: 'notifikationer', element: page(Pages.NotificationSettingsPage) },
              { path: 'moduler', element: page(Pages.ModulesPage) },
              { path: 'members', element: page(Pages.MembersPage) },
            ],
          },

          // Platformadministrationen. Egen rutegren med sin egen vagt: den
          // ligger inde i appen, men handler om alle andre end den
          // organisation, brugeren står i.
          {
            path: 'platform',
            element: <PlatformRoute />,
            children: [
              {
                element: <PlatformLayout />,
                children: [
                  { index: true, element: page(Pages.PlatformOverviewPage) },
                  { path: 'kunder', element: page(Pages.PlatformCustomersPage) },
                  {
                    path: 'kunder/:organizationId',
                    element: page(Pages.PlatformCustomerPage),
                  },
                  { path: 'brugere', element: page(Pages.PlatformUsersPage) },
                ],
              },
            ],
          },

          // Ukendte stier under /app fanges her frem for at ende i en tom skal.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
])
